/**
 * Pure derivation logic for the assignee name -> Auth UID map. NO imports (no
 * JSON, no module.exports) so this single file is loadable UNCHANGED in every
 * environment: CRA/webpack (via taskAssignees.js) and Node ESM (via the migration
 * scripts). It holds the LOGIC; `src/canon/people.json` holds the DATA — one
 * source each.
 *
 * Why this file exists (Slice 1a): a `.json` import needs an `with { type: 'json' }`
 * attribute in Node 22 ESM, but CRA's build (babel-preset-react-app) re-emits that
 * attribute in a legacy syntax webpack then rejects — so no single JSON-import
 * statement parses in BOTH. The fix: each environment does its OWN env-appropriate
 * JSON import (webpack = bare, Node = attribute) and both feed the people doc into
 * these shared pure functions. That keeps people.json the one data source and this
 * the one logic source, with zero duplicated map-building.
 */

/**
 * Build a name (lowercased alias) -> canonical Auth UID map from the people canon
 * doc (src/canon/people.json shape). People are walked in canon `order` so the map
 * is deterministic.
 * @param {{ people?: Array<{ authUid: string, aliases?: string[], order?: number }> }} peopleDoc
 * @returns {Record<string, string>}
 */
export function buildNameToUid(peopleDoc) {
	const map = {};
	const roster = [...((peopleDoc && peopleDoc.people) || [])].sort(
		(a, b) => (a.order || 0) - (b.order || 0)
	);
	for (const person of roster) {
		for (const alias of person.aliases || []) {
			const key = String(alias || '').trim().toLowerCase();
			if (key) map[key] = person.authUid;
		}
	}
	return map;
}

/**
 * Map an array of assignee display names to canonical Auth UIDs using a prebuilt
 * name->UID map. Matches case-insensitively on the trimmed name; unresolved names
 * are dropped (all-unresolved -> []). Returns a de-duped array.
 * @param {string[]} assignedTo
 * @param {Record<string, string>} nameToUid
 * @returns {string[]}
 */
export function deriveAssigneeUidsFrom(assignedTo, nameToUid) {
	const uids = [];
	for (const name of assignedTo || []) {
		const key = String(name || '').trim().toLowerCase();
		if (!key) continue;
		const uid = nameToUid[key];
		if (uid && !uids.includes(uid)) uids.push(uid);
	}
	return uids;
}
